import type { ColumnDef } from '@tanstack/react-table';
import { ClipboardList, Edit3, Plus, Trash2 } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BulkActionBar } from '@/components/ui/bulk-action-bar';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { SavedFilterMenu } from '@/components/ui/saved-filter-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipment } from '@/features/equipment/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import {
  useWorkOrders,
} from '@/features/work-orders/queries';
import type { ListWorkOrdersFilters } from '@/features/work-orders/service';
import {
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
  WORK_ORDER_TYPES,
  workOrderPriorityColor,
  workOrderStatusColor,
  type WorkOrder,
  type WorkOrderStatus,
  type WorkOrderType,
} from '@/features/work-orders/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/auth/store';
import { hasAnyRole } from '@/auth/types';
import { BulkDeleteModal } from './BulkDeleteModal';
import { BulkPriorityModal } from './BulkPriorityModal';

function ListSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 7 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Format an ISO date for the "Due" column. Returns null when null. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function WorkOrdersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canBulkUpdate =
    user !== null &&
    hasAnyRole(user, ['isi_admin', 'superadmin', 'fleet_manager', 'chief']);
  const canBulkDelete =
    user !== null && hasAnyRole(user, ['isi_admin', 'superadmin']);

  const filters: ListWorkOrdersFilters = {
    vesselId: searchParams.get('vesselId') ?? undefined,
    status: (searchParams.get('status') as WorkOrderStatus) ?? undefined,
    type: searchParams.get('type') ?? undefined,
  };

  const { data, isLoading, isError, error, refetch, isFetching } =
    useWorkOrders(filters);

  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list
      .filter((w) =>
        priorityFilter === '' ? true : w.priority === priorityFilter,
      )
      .filter((w) =>
        matchesQuery(w, deferredSearch, [
          (x) => x.number,
          (x) => x.title,
          (x) => x.description,
          (x) => x.type,
        ]),
      );
  }, [data, deferredSearch, priorityFilter]);

  // L16 — selection state for bulk actions. Using a Set keeps add/
  // remove O(1); we serialize to/from the table cell via has()/add()
  // so toggling a row checkbox doesn't re-render the whole table.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const visibleIds = useMemo(() => filtered.map((w) => w.id), [filtered]);
  const allVisibleSelected =
    visibleIds.length > 0 &&
    visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };
  const toggleId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const onBulkSuccess = () => {
    clearSelection();
    void refetch();
  };

  const applySavedFilter = (filter: { filterJson: Record<string, unknown> }) => {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(filter.filterJson)) {
      if (v !== undefined && v !== null && v !== '') {
        next.set(k, String(v));
      }
    }
    setSearchParams(next, { replace: false });
    clearSelection();
  };

  // Resolve foreign keys to display names client-side. The vessel
  // and equipment lists are usually already cached by the time the
  // user hits this page (sidebar warm-up), so this is essentially
  // free; we fall back to the bare id if not.
  const { data: vessels } = useVessels();
  const { data: equipment } = useEquipment(
    filters.vesselId ? { vesselId: filters.vesselId } : {},
  );

  const vesselNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of vessels ?? []) m.set(v.id, vesselDisplayName(v));
    return m;
  }, [vessels]);

  const equipmentTagById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of equipment ?? []) m.set(e.id, e.tag);
    return m;
  }, [equipment]);

  const setFilter = (key: keyof ListWorkOrdersFilters, next: string | null) => {
    if (next) searchParams.set(String(key), next);
    else searchParams.delete(String(key));
    setSearchParams(searchParams, { replace: true });
  };

  const columns = useMemo<ColumnDef<WorkOrder>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <input
            type="checkbox"
            aria-label={t('bulk.selectAll')}
            data-testid="bulk-select-all"
            className="h-4 w-4 cursor-pointer"
            checked={allVisibleSelected}
            ref={(el) => {
              if (el) el.indeterminate = someVisibleSelected;
            }}
            onChange={toggleAllVisible}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={t('bulk.selectRow', { number: row.original.number })}
            data-testid={`bulk-select-${row.original.id}`}
            className="h-4 w-4 cursor-pointer"
            checked={selectedIds.has(row.original.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              toggleId(row.original.id);
            }}
          />
        ),
      },
      {
        accessorKey: 'number',
        header: () => t('workOrder.columns.number'),
        cell: ({ row }) => (
          <Link
            to={`/work-orders/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-sm text-primary hover:underline"
          >
            {row.original.number}
          </Link>
        ),
      },
      {
        accessorKey: 'title',
        header: () => t('workOrder.columns.title'),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: () => t('workOrder.columns.status'),
        cell: ({ getValue }) => {
          const s = getValue<WorkOrderStatus>();
          return (
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                workOrderStatusColor(s),
              )}
            >
              {t(`workOrder.status.${s}`)}
            </span>
          );
        },
      },
      {
        accessorKey: 'priority',
        header: () => t('workOrder.columns.priority'),
        cell: ({ row }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
              workOrderPriorityColor(row.original.priority),
            )}
          >
            {t(`workOrder.priority.${row.original.priority}`)}
          </span>
        ),
      },
      {
        accessorKey: 'type',
        header: () => t('workOrder.columns.type'),
        cell: ({ getValue }) => t(`workOrder.type.${getValue<WorkOrderType>()}`),
      },
      {
        id: 'equipment',
        header: () => t('workOrder.columns.equipment'),
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {equipmentTagById.get(row.original.equipmentId) ?? '—'}
          </span>
        ),
      },
      {
        id: 'vessel',
        header: () => t('workOrder.columns.vessel'),
        cell: ({ row }) => vesselNameById.get(row.original.vesselId) ?? '—',
      },
      {
        accessorKey: 'dueAt',
        header: () => t('workOrder.columns.dueAt'),
        cell: ({ getValue }) => formatDate(getValue<string | null>()) ?? '—',
      },
    ],
    // The `toggle*` and `selectedIds` setters/state are stable enough
    // for our purposes — eslint can't see through the closure but the
    // column defs are intentionally re-created on selection changes
    // so the header checkbox tracks `allVisibleSelected`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      equipmentTagById,
      vesselNameById,
      selectedIds,
      allVisibleSelected,
      someVisibleSelected,
    ],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('workOrder.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('workOrder.list.loadError');
  })();

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('workOrder.list.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('workOrder.list.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-64"
            data-testid="work-orders-search"
          />
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('workOrder.list.filterByVessel')}
            </span>
            <VesselSelect
              value={filters.vesselId ?? null}
              onChange={(v) => setFilter('vesselId', v)}
              className="w-48"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('workOrder.list.filterByStatus')}
            </span>
            <select
              value={filters.status ?? ''}
              onChange={(e) => setFilter('status', e.target.value || null)}
              className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('workOrder.list.allStatuses')}</option>
              {WORK_ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`workOrder.status.${s}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('workOrder.list.filterByType')}
            </span>
            <select
              value={filters.type ?? ''}
              onChange={(e) => setFilter('type', e.target.value || null)}
              className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('workOrder.list.allTypes')}</option>
              {WORK_ORDER_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`workOrder.type.${tp}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('workOrder.list.filterByPriority')}
            </span>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="flex h-10 w-32 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="work-orders-priority-filter"
            >
              <option value="">{t('workOrder.list.allPriorities')}</option>
              {WORK_ORDER_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`workOrder.priority.${p}`)}
                </option>
              ))}
            </select>
          </label>
          <SavedFilterMenu
            scope="WO"
            currentFilter={{
              vesselId: filters.vesselId ?? '',
              status: filters.status ?? '',
              type: filters.type ?? '',
            }}
            onApply={applySavedFilter}
          />
          <Button asChild>
            <Link to="/work-orders/new">
              <Plus aria-hidden />
              {t('workOrder.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('workOrder.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListSkeleton />}
          emptyState={
            <EmptyState
              icon={ClipboardList}
              title={t('workOrder.list.empty')}
              description={t('workOrder.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/work-orders/new">
                    <Plus aria-hidden />
                    {t('workOrder.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(wo) => navigate(`/work-orders/${wo.id}`)}
        />
      )}

      <BulkActionBar count={selectedIds.size} onClear={clearSelection}>
        {canBulkUpdate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPriorityOpen(true)}
            data-testid="bulk-action-priority"
          >
            <Edit3 aria-hidden className="mr-1 h-4 w-4" />
            {t('bulk.priority.button')}
          </Button>
        )}
        {canBulkDelete && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={() => setDeleteOpen(true)}
            data-testid="bulk-action-delete"
          >
            <Trash2 aria-hidden className="mr-1 h-4 w-4" />
            {t('bulk.delete.button')}
          </Button>
        )}
      </BulkActionBar>

      <BulkPriorityModal
        open={priorityOpen}
        onOpenChange={setPriorityOpen}
        ids={[...selectedIds]}
        onSuccess={onBulkSuccess}
      />
      <BulkDeleteModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        ids={[...selectedIds]}
        onSuccess={onBulkSuccess}
      />
    </div>
  );
}
